/** @file Mirrors -L/-R bones onto their counterparts (created or overwritten) across x = 0. */
import { z } from 'zod';
import type { Draft } from 'immer';
import { docSetupWorlds } from '@pose2d/core/pose/docSetupWorlds';
import { affine } from '@pose2d/math/affine';
import { mirrorName } from '@pose2d/format/doc/mirrorName';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id, LocalTransform } from '@pose2d/format/types';
import { invariant } from '@pose2d/std/invariant';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { localFromWorld } from './localFromWorld';
import { nextSiblingIndex } from './nextSiblingIndex';

/** Reflection x → -x of skeleton space, and the flip of the bone's own Y axis that keeps its handedness. */
const MIRROR_X = [-1, 0, 0, 1, 0, 0];
const FLIP_Y = [1, 0, 0, -1, 0, 0];
const SETUP_KEYS = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'shearX', 'shearY'] as const;

type Picked = { id: Id; name: string; twin: string; at: string };
type Worlds = ReadonlyMap<string, Float64Array>;

/** The setup world of `id` from a docSetupWorlds map (W2-14); the map covers every bone, so a miss is a bug. */
function worldIn(worlds: Worlds, id: Id): Float64Array {
  const world = worlds.get(id);
  invariant(world, `docSetupWorlds has no world for bone ${id}`);
  return world;
}

/** Number of ancestors (parents are mirrored before their children, so arm-R exists before hand-R). */
function depth(doc: Doc, id: Id): number {
  let n = 0;
  for (let p = doc.bones[id]?.parent ?? null; p !== null; p = doc.bones[p]?.parent ?? null) n++;
  return n;
}

/** A selected bone that can be mirrored, or null after an INFO warning for a name without -L/-R. */
function pickOne(doc: Doc, id: Id, at: string, ctx: CommandContext): Picked | null {
  const bone = doc.bones[id];
  if (!bone) return null;
  if (bone.kind !== undefined) throwError('VALIDATION', undefined, { path: at, hint: 'generated socket bone: edit the socket instead' });
  if (bone.parent === null) throwError('VALIDATION', undefined, { path: at, hint: 'the root bone cannot be mirrored' });
  const twin = mirrorName(bone.name);
  if (twin === null) ctx.warnings.push({ code: 'INFO', message: `bone "${bone.name}" has no -L/-R suffix and was not mirrored`, params: { bone: bone.name }, path: at });
  return twin === null ? null : { id, name: bone.name, twin, at };
}

/** The mirrorable selection, parents first; VALIDATION when both sides are selected or nothing is sided. */
function pick(doc: Doc, refs: readonly string[], ctx: CommandContext): Picked[] {
  const seen = new Set<Id>();
  const picked: Picked[] = [];
  refs.forEach((ref, i) => {
    const id = ctx.resolve('bone', ref);
    const one = seen.has(id) ? null : pickOne(doc, id, `bones.${i}`, ctx);
    seen.add(id);
    if (one) picked.push(one);
  });
  const selected = new Set(picked.map((p) => p.name));
  const both = picked.find((p) => selected.has(p.twin));
  if (both) throwError('VALIDATION', undefined, { path: both.at, hint: `${both.name} and ${both.twin} are both selected; select one side only` });
  if (picked.length === 0) throwError('VALIDATION', undefined, { path: 'bones', hint: 'none of the bones has a -L/-R name to mirror' });
  return picked.sort((a, b) => depth(doc, a.id) - depth(doc, b.id));
}

/** Id of the bone called `name` before this command or created by it, if any. */
const boneNamed = (ctx: CommandContext, name: string): Id | undefined => ctx.created[name] ?? ctx.index.byName.get('bone')?.get(name);

/** World setup matrix of the counterpart: MIRROR_X × world × FLIP_Y (mirrored place and direction, same handedness). */
function mirroredWorld(worlds: Worlds, p: Picked): number[] {
  const out = [0, 0, 0, 0, 0, 0];
  affine.mul(out, MIRROR_X, worldIn(worlds, p.id));
  affine.mul(out, out, FLIP_Y);
  return out;
}

/** Adds the counterpart under the counterpart of the source's parent (else the same parent) and reports it in `created`. */
function createTwin(draft: Draft<Doc>, p: Picked, worlds: Worlds, world: number[], ctx: CommandContext): boolean {
  const src = draft.bones[p.id];
  if (!src?.parent) return false;
  const parentTwin = mirrorName(draft.bones[src.parent]?.name ?? '');
  const parent = (parentTwin === null ? undefined : boneNamed(ctx, parentTwin)) ?? src.parent;
  const setup = localFromWorld(worldIn(worlds, parent), world, p.at);
  const id = ctx.newId('bone');
  draft.bones[id] = { name: p.twin, parent, siblingIndex: nextSiblingIndex(draft, parent), length: src.length, inherit: 'normal', setup };
  ctx.created[p.twin] = id;
  return true;
}

/** True when two setups are equal field by field (both come from localFromWorld rounding, so equality is exact). */
const sameSetup = (a: LocalTransform, b: LocalTransform): boolean => SETUP_KEYS.every((k) => a[k] === b[k]);

/** Rewrites an existing counterpart's setup, length and inherit (its parent is kept); false when nothing changes. */
function updateTwin(draft: Draft<Doc>, twinId: Id, p: Picked, worlds: Worlds, world: number[]): boolean {
  const twin = draft.bones[twinId];
  if (!twin) return false;
  if (twin.kind !== undefined || twin.parent === null) {
    throwError('VALIDATION', undefined, { path: p.at, hint: `${p.twin} is the root or a generated socket bone and cannot be overwritten` });
  }
  const setup = localFromWorld(worldIn(worlds, twin.parent), world, p.at);
  const length = draft.bones[p.id]?.length ?? twin.length;
  if (sameSetup(twin.setup, setup) && twin.length === length && twin.inherit === 'normal') return false;
  Object.assign(twin, { setup, length, inherit: 'normal' });
  return true;
}

/** INFO at the selection entry: the counterpart exists and overwrite was not asked for. */
const warnExisting = (ctx: CommandContext, p: Picked): void => {
  ctx.warnings.push({ code: 'INFO', message: `bone "${p.twin}" already exists; pass overwrite: true to replace its setup`, params: { bone: p.twin }, path: p.at });
};

/** bone.mirrorSelection: bones only (slots, attachments, IK and keys are untouched); counterparts inherit normal. */
export const command = defineCommand({
  name: 'bone.mirrorSelection',
  description: 'Mirror -L/-R bones across the skeleton x = 0 axis onto their counterparts (arm-L → arm-R, rotation 30 → 150). A missing counterpart is created under the counterpart of the parent (else the same parent); an existing one is skipped unless overwrite: true. Bones only; names without -L/-R are skipped with a warning. Counterparts use inherit "normal" and match the mirrored setup world through any ancestor inherit mode.',
  input: z.object({ bones: z.array(z.string().min(1)).min(1), overwrite: z.boolean().exactOptional() }).strict(),
  apply(draft, input, ctx) {
    let changed = 0;
    for (const p of pick(draft, input.bones, ctx)) {
      const existing = boneNamed(ctx, p.twin);
      if (existing !== undefined && input.overwrite !== true) {
        warnExisting(ctx, p);
        continue;
      }
      const worlds = docSetupWorlds(draft);
      const world = mirroredWorld(worlds, p);
      const wrote = existing === undefined ? createTwin(draft, p, worlds, world, ctx) : updateTwin(draft, existing, p, worlds, world);
      if (wrote) changed++;
    }
    if (changed > 0) draft.meta.updatedAt = ctx.now;
  },
});
